import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { Package, LogOut, User } from "lucide-react";
import ChangePasswordForm from "@/components/ChangePasswordForm";

const STATUS_COLOR = {
    pending: "bg-accent/10 text-accent",
    processing: "bg-primary/10 text-primary",
    dispatched: "bg-secondary/10 text-secondary",
    delivered: "bg-secondary text-white",
    cancelled: "bg-muted text-muted-foreground",
};

export default function Profile() {
    const { user, logout, checked } = useAuth();
    const nav = useNavigate();
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (checked && !user) nav("/login");
    }, [checked, user, nav]);

    useEffect(() => {
        if (user) api.get("/my-orders").then((r) => setOrders(r.data)).catch(() => {});
    }, [user]);

    if (!user) return null;

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12" data-testid="profile-page">
            <div className="grid md:grid-cols-[280px_1fr] gap-8">
                <aside className="bg-white border border-border p-6 h-fit">
                    <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center font-display font-bold text-xl mb-3">
                        {user.name[0]?.toUpperCase()}
                    </div>
                    <div className="font-display font-bold text-lg">{user.name}</div>
                    <div className="text-sm text-muted-foreground">{user.email}</div>
                    <div className="text-xs text-muted-foreground mt-1 capitalize">Role: {user.role}</div>
                    {user.role === "admin" && (
                        <Link to="/admin" className="block mt-4 text-center bg-secondary text-white py-2 text-sm font-semibold">
                            Open admin
                        </Link>
                    )}
                    <button onClick={() => { logout(); nav("/"); }} className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-border py-2 text-sm font-semibold hover:bg-muted" data-testid="profile-logout">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </aside>

                <div>
                    <h1 className="font-display text-3xl md:text-4xl font-bold mb-6 flex items-center gap-2">
                        <Package className="w-6 h-6 text-primary" /> Your orders
                    </h1>
                    {orders.length === 0 ? (
                        <div className="bg-surface border border-border p-8 text-center text-muted-foreground">
                            You haven't placed any orders yet. <Link to="/products" className="text-primary font-semibold">Start shopping</Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {orders.map((o) => (
                                <div key={o.id} className="bg-white border border-border p-5" data-testid={`order-${o.order_number}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="font-mono font-bold">{o.order_number}</div>
                                            <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</div>
                                        </div>
                                        <span className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold ${STATUS_COLOR[o.status] || "bg-muted"}`}>
                                            {o.status}
                                        </span>
                                    </div>
                                    <div className="text-sm space-y-1">
                                        {o.items.slice(0, 3).map((it, i) => (
                                            <div key={i} className="flex justify-between">
                                                <span className="text-muted-foreground">{it.name} × {it.quantity}</span>
                                                <span>{money(it.price * it.quantity)}</span>
                                            </div>
                                        ))}
                                        {o.items.length > 3 && <div className="text-xs text-muted-foreground">+ {o.items.length - 3} more</div>}
                                    </div>
                                    <div className="border-t border-border mt-3 pt-3 flex justify-between font-semibold">
                                        <span>Total</span>
                                        <span>{money(o.total)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <ChangePasswordForm />
                </div>
            </div>
        </div>
    );
}
